import { takeEvery, all, put, select, call, race, take, delay } from 'redux-saga/effects';
import * as actions from './actions';
import * as selectors from './selectors';
import * as dailyBatchHistApi from '../../api/dailyBatchHistApi';
import { searchToFilters } from '../../common/urlUtils';
import actionFactory from '../../webcommon/header/filterheader/actions';
import * as filterHeaderSelectors from '../../webcommon/header/filterheader/selectors';
import { DETAIL_REFRESH_RATE } from './constants';

const filterHeaderId = 'DailyBatchHist';

export const filterHeaderActions = actionFactory(filterHeaderId);

export function* dailyBatchHistConnected(action){
    try {
        const initialised = yield (select(selectors.getDailyBatchHistInitialised));
        if (!initialised) {
            yield put(actions.setDailyBatchHistLoading(true));
            const filterDefinitions = yield select(selectors.getFilterDefinitions);
            yield put(filterHeaderActions.setFilterDefinitions(filterDefinitions));
            const defaultFilters = yield select(selectors.getDefaultFilters);
            yield put(filterHeaderActions.setDefaultFilters(defaultFilters));
            if (action.search === '') {
                yield put(filterHeaderActions.resetFilters());
            } else {
                const filters = searchToFilters(action.search, filterDefinitions, defaultFilters);
                yield put(filterHeaderActions.setFilters(filters));
            }
            yield put(actions.getDailyBatchHistory());

            yield put(actions.setDailyBatchHistInitialised(true));
        }
    } finally {
        yield put(actions.setDailyBatchHistLoading(false));
    }
}

export function* filterChanged(action) {
    const initialised = yield select(selectors.getDailyBatchHistInitialised);
    if (initialised) {
        const currentlyOpenFilter = yield (select(filterHeaderSelectors.getCurrentlyOpenFilter, filterHeaderId));
        if (!currentlyOpenFilter) {
            const filters = yield select(filterHeaderSelectors.getFilters, filterHeaderId);
            yield put(actions.setDateFrom(filters[0].value.from));
            yield put(actions.setDateTo(filters[0].value.to));
            yield put(actions.refreshDailyBatchHist());
        }
    }
} 

export function* getDailyBatchHistory(api, action) {
    try {
        const filters = yield select(filterHeaderSelectors.getFilters, filterHeaderId);
        const dateFrom = selectors.getDateFrom(filters);
        const dateTo = selectors.getDateTo(filters);
        const result = yield call(api.getDailyBatchHistory, dateFrom, dateTo);
        yield put(actions.getDailyBatchHistSuccess(result));
    } catch (error) {
        yield put(actions.getDailyBatchHistFailed('Failed to retrieve daily batch history'));
    }
}





export function* fetchRunStatus(api, runId) {
    try {
        const detail = yield call(api.getRunStatus, runId);
        yield put(actions.getRunStatusSuccess(detail));
        return detail;
    } catch (error) {
        yield put(actions.getRunStatusFailed('Failed to retrieve run status'));
        return null;
    }
}

export function* selectRunSaga(api, action) {
    let detail = yield call(fetchRunStatus, api, action.runId);

    // Keep polling every 10s while the batch is still running
    while (detail && detail.overallStatus === 'Running') {
        const { cancelled } = yield race({
            delayed: delay(DETAIL_REFRESH_RATE),
            cancelled: take(actions.DESELECT_RUN),
        });
        if (cancelled) break;
        // Re-check that the same run is still selected before fetching
        const currentRunId = yield select(selectors.getSelectedRunId);
        if (currentRunId !== action.runId) break;
        detail = yield call(fetchRunStatus, api, action.runId);
    }
}

export default function* rootSaga() {
    yield all([
        takeEvery(actions.DAILY_BATCH_HIST_LOADED, dailyBatchHistConnected),
        takeEvery(
            [
                filterHeaderActions.UPDATE_FILTER,
                filterHeaderActions.REMOVE_FILTER,
                filterHeaderActions.RESET_FILTERS
            ],
            filterChanged),
        takeEvery(
            [
                actions.GET_DAILY_BATCH_HISTORY,
                actions.REFRESH_DAILY_BATCH_HIST
            ],
            getDailyBatchHistory, dailyBatchHistApi),
        takeEvery(actions.SELECT_RUN, selectRunSaga, dailyBatchHistApi),
    ]);
}